"use client";

import { ArrowRight, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PartnerQualifications() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const principles = [{
    number: "01",
    eyebrow: "CREDIT",
    title: "Credit Profile",
    body: "We look for partners with a FICO score of 700 or higher. This baseline ensures the professional-grade reliability required for our joint venture capital structures.",
    icon: "shield"
  }, {
    number: "02",
    eyebrow: "CAPITAL",
    title: "Available Liquidity",
    body: "Partners should demonstrate accessible capital reserves. We prioritize liquidity to ensure every deal is fully funded and ready for immediate execution without delays.",
    icon: "trending"
  }, {
    number: "03",
    eyebrow: "EXPERIENCE",
    title: "Proven Track Record",
    body: "Whether you are a seasoned investor or a new entrant, we value demonstrated real estate experience. Your history of successful projects is our primary metric for partnership.",
    icon: "users"
  }];
  return <section data-section-id="425" id="partner-qualifications" className="relative overflow-hidden bg-background py-24 md:py-32">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 opacity-[0.07]">
        <svg viewBox="0 0 200 200" fill="none" className="h-full w-full text-primary">
          <path d="M20 180 Q40 140 60 130 Q80 120 90 100 Q100 80 120 70 Q140 60 160 40" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          <path d="M55 135 Q45 145 35 145 Q45 135 55 135 Z" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M85 105 Q75 115 65 115 Q75 105 85 105 Z" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M115 75 Q105 85 95 85 Q105 75 115 75 Z" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M145 45 Q135 55 125 55 Q135 45 145 45 Z" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-64 w-64 opacity-[0.07]">
        <svg viewBox="0 0 200 200" fill="none" className="h-full w-full text-primary">
          <path d="M180 180 Q160 140 140 130 Q120 120 110 100 Q100 80 80 70 Q60 60 40 40" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          <path d="M145 135 Q155 145 165 145 Q155 135 145 135 Z" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M115 105 Q125 115 135 115 Q125 105 115 105 Z" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M85 75 Q95 85 105 85 Q95 75 85 75 Z" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M55 45 Q65 55 75 55 Q65 45 55 45 Z" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <p className="font-default text-xs font-medium uppercase tracking-[0.3em] text-secondary">
            Partner Qualifications
          </p>
          <h2 className="font-default mt-6 text-4xl font-bold leading-[1.1] tracking-tight text-foreground md:text-5xl lg:text-[52px]">
            Architecting Capital Together.
          </h2>
          <div className="mt-8 flex justify-center">
            <span className="h-px w-12 bg-primary/60" aria-hidden="true" />
          </div>
        </div>

        {}
        <div ref={gridRef} className="relative mt-20 grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-8 lg:gap-12">
          {principles.map((item, i) => <div key={i} data-index={i} className={`relative flex flex-col items-center text-center transition-all duration-1000 ease-out ${gridVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
          transitionDelay: gridVisible ? `${i * 150}ms` : "0ms"
        }}>
              {}
              {i > 0 && <div className="absolute -left-4 top-8 hidden h-40 w-px bg-border md:block lg:-left-6" aria-hidden="true" />}

              {}
              <div className="relative flex h-[120px] w-[120px] items-center justify-center rounded-lg">
                <div className="absolute inset-0 rounded-lg bg-card" />
                <div className="absolute inset-0 border border-primary/40" aria-hidden="true" />
                <div className="absolute inset-2 border border-primary/20" aria-hidden="true" />
                <div className="relative flex items-center justify-center text-primary">
                  {item.icon === "shield" && <ShieldCheck className="h-12 w-12" strokeWidth={1} />}
                  {item.icon === "trending" && <TrendingUp className="h-12 w-12" strokeWidth={1} />}
                  {item.icon === "users" && <Users className="h-12 w-12" strokeWidth={1} />}
                </div>
              </div>

              {}
              <div className="mt-8 flex items-center justify-center gap-3">
                <span className="font-mono text-xs font-medium text-primary">
                  {item.number}
                </span>
                <span className="font-default text-xs font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  {item.eyebrow}
                </span>
              </div>

              {}
              <h3 className="font-default mt-4 text-2xl font-semibold leading-snug tracking-tight text-foreground">
                {item.title}
              </h3>

              {}
              <p className="font-default mt-5 max-w-xs text-[15px] leading-[1.75] text-muted-foreground">
                {item.body}
              </p>
            </div>)}
        </div>

        {}
        <div ref={footerRef} className={`mt-20 flex flex-col items-center transition-all duration-1000 ease-out ${footerVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <Separator className="mb-10 w-16 bg-border" />
          <Link to="/PreQualificationFormPage" className="font-default group inline-flex items-center gap-2 text-sm font-medium text-primary transition-colors hover:text-secondary">
            <span className="border-b border-primary/40 pb-0.5 transition-colors group-hover:border-secondary">
              Partner With Us
            </span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}